import type { ReactNode } from "react";
import { Copy } from "lucide-react";
import type { PrequalInviteDto } from "@/api/buyers";
import {
  Button,
  Skeleton,
  StatusBadge,
  Timestamp,
  Tooltip,
  TruncateCell,
  type SubTableColumn,
} from "@/design-system/components/ui";
import {
  PREQUAL_INVITE_STATUS_TONE,
  isTerminalInviteStatus,
  terminalInviteReason,
} from "@/design-system/lib/prequalInviteStatus";
import { InlineQueryError } from "../queryStates";

/**
 * The parts the buyer and seller invitation surfaces share.
 *
 * <p>The two shipped as near-duplicates: the same row, the same duplicate
 * confirm, the same clipboard behaviour, the same loading / failed / empty / list
 * scaffold, differing only in a noun. That is four places to fix a rendering bug
 * and two places to get the status vocabulary wrong.</p>
 *
 * <p>🔴 <b>What is NOT shared is the composition step, and that is a real
 * difference rather than an oversight.</b> The buyer's address comes from their
 * own record, so there is nothing to type and the invitation is one click. This
 * module holds no seller contact record at all, so the seller's address must be
 * asked for — and, since asking means a typed address that nobody has verified,
 * it goes through the send preview first. Collapsing those into one form would
 * mean either asking the buyer surface for an address it already has, or letting
 * a hand-typed seller address go out unreviewed.</p>
 *
 * <p>⚠️ Distinct again from <c>components/prequalification/SendPrequalInviteModal</c>,
 * which is NOT a duplicate of either and is live at three mount points across
 * two staff pages. That one starts a Phoenix-side funnel — it carries a partner-brand
 * picker and two variants that open an assisted draft a staff member fills in
 * WITH the person. These two send a link scoped to one buyer or one submission
 * and list what has been sent against that record. Different job, different
 * audience, no convergence available.</p>
 */

/**
 * The invitation list's columns.
 *
 * <p>🔴 <b>A TABLE, and every fact the hand-rolled row carried is still on it.</b>
 * This was a <c>&lt;ul&gt;</c> of two-line rows joined by middots, which is a
 * table that has to be re-read to be scanned: the lifecycle clauses appeared and
 * disappeared per row, so "which of these has been opened" could only be answered
 * by reading each one. Headed columns answer it by looking. Nothing is flattened
 * away — the four lifecycle stamps keep their own clauses inside one cell,
 * because they are a SEQUENCE and a column each would be four mostly-empty
 * columns.</p>
 *
 * <p>🔴 <b>The status is rendered exactly as the server gives it, and expiry is
 * NEVER recomputed here.</b> <c>PrequalInviteDto.status</c> is already the
 * EFFECTIVE value — the persisted lifecycle with derived expiry folded in by
 * <c>PrequalificationInviteLifecycle.Effective</c>. A client-side clock comparison
 * would be a second implementation of that rule, drifting the moment the server
 * changed its horizon, and it is the kind of drift nobody notices because both
 * answers look plausible.</p>
 *
 * <p><b>Every column that can hold two lines is <c>align-top</c>.</b> A cell
 * vertically centred against a four-clause neighbour floats in the middle of the
 * row with no relationship to the line it belongs to.</p>
 */
export function inviteColumns({
  canEdit,
  recipientNoun,
  rowContext,
  resending,
  onResend,
  onCopy,
}: {
  canEdit: boolean;
  /** "buyer" or "seller" — the only word that differs between the two. */
  recipientNoun: string;
  /** Extra provenance under the address. The buyer surface names the listing an
   *  invitation was sent for; the seller surface has nothing to add. */
  rowContext?: (invite: PrequalInviteDto) => ReactNode;
  resending: boolean;
  onResend: (inviteId: string) => void;
  onCopy: (invite: PrequalInviteDto) => void;
}): SubTableColumn<PrequalInviteDto>[] {
  return [
    {
      key: "recipient",
      header: "Invitation",
      className: "align-top",
      // The email can be long and the column has to be able to give way; the
      // full value comes back on hover AND on keyboard focus, which a native
      // `title` cannot do.
      searchText: (i) => i.email,
      render: (i) => {
        const context = rowContext?.(i);
        return (
          <span className="grid min-w-0 gap-space-1">
            <TruncateCell className="text-small text-onyx">{i.email}</TruncateCell>
            {context && <span className="text-caption text-gray-mid">{context}</span>}
          </span>
        );
      },
    },
    {
      key: "status",
      header: "Status",
      className: "align-top",
      sortAccessor: (i) => i.status,
      searchText: (i) => i.status,
      render: (i) => (
        <StatusBadge tone={PREQUAL_INVITE_STATUS_TONE[i.status]}>{i.status}</StatusBadge>
      ),
    },
    {
      key: "lifecycle",
      header: "Lifecycle",
      className: "align-top",
      // The stamp the reader actually orders by. Sorting on the rendered
      // clauses would sort on the word "Sent".
      sortAccessor: (i) => new Date(i.sentAtUtc),
      searchText: () => "",
      render: (i) => (
        <span className="grid gap-space-1 text-caption text-gray-mid">
          <span>
            Sent <Timestamp value={i.sentAtUtc} />
            {i.sendCount > 1 && ` · ${i.sendCount} sends`}
          </span>
          {i.openedAtUtc && (
            <span>
              Opened <Timestamp value={i.openedAtUtc} />
            </span>
          )}
          {/* On the wire since 3a. The status could always say a recipient had
              STARTED; it could not say when. */}
          {i.startedAtUtc && (
            <span>
              Started <Timestamp value={i.startedAtUtc} />
            </span>
          )}
          {i.completedAtUtc && (
            <span>
              Completed <Timestamp value={i.completedAtUtc} />
            </span>
          )}
        </span>
      ),
    },
    {
      key: "sentBy",
      header: "Sent by",
      className: "align-top",
      sortAccessor: (i) => i.invitedByLabel ?? "",
      searchText: (i) => i.invitedByLabel ?? "",
      // A plain missing value, not a workflow state: nobody can go and record
      // who sent an invitation after the fact, so it gets the em dash and no
      // door. (Table and Tile Standard v1.0 §7.)
      render: (i) =>
        i.invitedByLabel ? (
          <span className="text-caption text-gray-mid">{i.invitedByLabel}</span>
        ) : (
          <span className="text-gray-mid">
            <span aria-hidden>—</span>
            <span className="sr-only">Not recorded</span>
          </span>
        ),
    },
    {
      key: "actions",
      header: "Actions",
      className: "align-top",
      searchText: () => "",
      render: (i) => {
        const terminal = isTerminalInviteStatus(i.status);
        const reason = terminalInviteReason(i.status);

        return (
          <span className="flex shrink-0 items-center gap-space-1">
            <Button variant="text" size="sm" onClick={() => onCopy(i)}>
              <Copy className="h-4 w-4" aria-hidden />
              Copy link
            </Button>
            {canEdit && (
              // "Nudge" resends to the RECIPIENT. The automated worker nudges
              // the SENDER — a different action to a different person, which is
              // why both exist and why the labels have to keep them apart.
              <Tooltip
                label={
                  (terminal ? reason : `Resend this invitation to the ${recipientNoun}`) ?? ""
                }
              >
                <span>
                  <Button
                    variant="text"
                    size="sm"
                    onClick={() => onResend(i.id)}
                    disabled={terminal || resending}
                  >
                    Nudge
                  </Button>
                </span>
              </Tooltip>
            )}
          </span>
        );
      },
    },
  ];
}

/**
 * A live invitation already exists. Surfaced as a confirm rather than swallowed —
 * the sender may genuinely want a second one, but never by accident.
 */
export function DuplicateInviteConfirm({
  testId,
  subject,
  pending,
  onConfirm,
  onCancel,
}: {
  testId: string;
  /** "This buyer" / "That address" — what already has a live invitation. */
  subject: string;
  pending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      data-testid={testId}
      className="rounded-md border border-status-warning bg-white-warm p-space-3 text-caption text-onyx"
    >
      <p className="m-0">
        {subject} already has a live invitation. Sending another will mail them a
        second link.
      </p>
      <div className="mt-space-2 flex gap-space-2">
        {/* The one act this strip exists to authorise, so it carries the
            section's primary weight — stated rather than inherited from the
            component default, which is how it acquired that weight before. */}
        <Button variant="primary" size="sm" onClick={onConfirm} disabled={pending}>
          Send anyway
        </Button>
        <Button variant="text" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/**
 * Loading or failed — the two states a table cannot answer for itself.
 *
 * <p>🔴 A failed read used to render as "no invitations yet" on both surfaces,
 * which is exactly the answer that gets a second link mailed to someone who
 * already has one. Loading, FAILED and empty are three states, never two.</p>
 *
 * <p><b>Empty is deliberately NOT here any more.</b> It moved into the table,
 * which knows its own row count — the old signature took an <c>isEmpty</c> flag
 * beside a <c>children</c> list precisely because counting rendered children
 * breaks the moment somebody wraps them in a fragment. Handing the table the
 * rows removes the question.</p>
 */
export function InviteListState({
  loading,
  error,
  retrying,
  onRetry,
  what,
  skeletonClassName = "h-24 w-full",
  children,
}: {
  loading: boolean;
  error: boolean;
  retrying: boolean;
  onRetry: () => void;
  what: string;
  skeletonClassName?: string;
  children: ReactNode;
}) {
  if (loading) return <Skeleton className={skeletonClassName} />;
  if (error) {
    return <InlineQueryError what={what} onRetry={onRetry} retrying={retrying} />;
  }
  return <>{children}</>;
}

/**
 * The server hands back the full link expressly so a sender can deliver it
 * through their own channel — often the only route to a seller who is being
 * introduced by their broker rather than approached directly.
 *
 * <p>🔴 Never construct one: a hand-built URL would miss the token and silently
 * produce a dead link.</p>
 */
export async function copyInviteLink(
  invite: PrequalInviteDto,
  toast: { success: (m: string) => void; error: (m: string) => void },
) {
  try {
    await navigator.clipboard.writeText(invite.inviteUrl);
    toast.success("Invitation link copied.");
  } catch {
    toast.error("Could not copy the link.");
  }
}
